import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CalendarClock, AlertTriangle, CarFront, Clock, FileSignature, Inbox, Phone, Share2, CheckCircle2, BellRing } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_DOT = {
  available: "bg-green-400",
  rented: "bg-red-400",
  maintenance: "bg-yellow-400",
};

function StatCard({ icon: Icon, label, value, tone = "default", testid }) {
  const tones = {
    default: "border-white/10 text-white",
    danger: "border-red-500/40 text-red-400",
    warn: "border-[#F2B705]/40 text-[#F2B705]",
    ok: "border-green-500/30 text-green-400",
  };
  return (
    <div data-testid={testid} className={`bg-[#0C0C0C] border rounded-xl p-4 ${tones[tone]}`}>
      <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-widest">
        <Icon size={15} /> {label}
      </div>
      <div className="font-display font-black text-3xl mt-2 leading-none">{value}</div>
    </div>
  );
}

function RentalRow({ r, tone, onReturned }) {
  const badge = tone === "overdue"
    ? <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/30">{Math.abs(r.days_remaining)}d overdue</span>
    : tone === "today"
    ? <span className="text-xs px-2 py-0.5 rounded-full bg-[#F2B705]/15 text-[#F2B705] border border-[#F2B705]/30">Due today</span>
    : <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-white/70">in {r.days_remaining}d</span>;
  return (
    <div data-testid={`ops-rental-${r.agreement_id}`} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold truncate">{r.renter_name} <span className="text-white/40 font-normal">· {r.vehicle}</span></div>
          <div className="text-white/45 text-xs mt-0.5 flex items-center gap-3">
            <span>{r.start_date} → {r.return_date}</span>
            {r.renter_phone && <a href={`tel:${r.renter_phone}`} className="flex items-center gap-1 text-[#F2B705] hover:underline"><Phone size={11} /> {r.renter_phone}</a>}
          </div>
        </div>
        {badge}
      </div>
      <button data-testid={`ops-mark-returned-${r.agreement_id}`} onClick={() => onReturned(r.agreement_id)}
        className="mt-2 flex items-center gap-1.5 text-xs border border-green-500/30 text-green-400 px-3 py-1.5 rounded hover:bg-green-500 hover:text-white transition-colors">
        <CheckCircle2 size={13} /> Mark Returned &amp; Request Review
      </button>
    </div>
  );
}

function Section({ title, icon: Icon, items, tone, empty, onReturned }) {
  return (
    <div>
      <h3 className="flex items-center gap-2 font-display font-bold text-lg mb-3"><Icon size={18} className={tone === "overdue" ? "text-red-400" : tone === "today" ? "text-[#F2B705]" : "text-white/60"} /> {title} <span className="text-white/35 text-sm font-normal">({items.length})</span></h3>
      {items.length === 0 ? <p className="text-white/35 text-sm mb-2">{empty}</p> : (
        <div className="space-y-2">{items.map((r) => <RentalRow key={r.agreement_id} r={r} tone={tone} onReturned={onReturned} />)}</div>
      )}
    </div>
  );
}

export default function OperationsDashboard({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extReqs, setExtReqs] = useState([]);

  const fetchData = () => {
    axios.get(`${API}/extension-requests?status=pending`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setExtReqs(r.data)).catch(() => {});
    return axios.get(`${API}/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  };

  const approveExt = async (req, force = false) => {
    try {
      const { data: res } = await axios.post(`${API}/extension-requests/${req.id}/approve?force=${force}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Extension approved — ${req.name} keeps the ${req.vehicle} until ${req.new_return_date}.${res.agreement_auto_signed ? " Agreement auto-signed & emailed." : ""}`);
      fetchData();
    } catch (e) {
      if (e?.response?.status === 409 && !force) {
        if (window.confirm(`⚠ ${e.response.data?.detail || "This extension overlaps another reservation."}`)) return approveExt(req, true);
        return;
      }
      toast.error(e?.response?.data?.detail || "Could not approve the extension.");
    }
  };

  const declineExt = async (req) => {
    const reason = window.prompt(`Decline ${req.name}'s extension request? Optional reason (sent to the customer):`, "");
    if (reason === null) return;
    try {
      await axios.post(`${API}/extension-requests/${req.id}/decline`, { reason }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Extension request declined — customer notified.");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not decline the request.");
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [token]);

  const markReturned = async (agreementId) => {
    if (!window.confirm("Mark this rental as returned? This will email the customer a review request.")) return;
    try {
      const { data: res } = await axios.post(`${API}/agreements/${agreementId}/mark-returned`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.review_sent_to ? `Marked returned. Review request sent to ${res.review_sent_to}.` : "Marked returned.");
      fetchData();
    } catch {
      toast.error("Could not mark returned.");
    }
  };

  const advanceStatus = async (bookingId, next, label) => {
    try {
      const { data: res } = await axios.post(`${API}/bookings/${bookingId}/status`, { lifecycle_status: next }, { headers: { Authorization: `Bearer ${token}` } });
      let extra = "";
      if (res.renter_notified) extra = " · customer notified";
      else if (res.vehicle_freed) extra = " · car freed" + (res.review_request_sent ? " + review email sent" : "");
      toast.success(`${label}${extra}`);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update status.");
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#F2B705]" /></div>;
  if (!data) return <p className="text-white/40">Could not load dashboard.</p>;

  const s = data.stats;
  const actionNeeded = data.action_needed || [];
  return (
    <div data-testid="operations-dashboard" className="space-y-8">
      {extReqs.length > 0 && (
        <div data-testid="ops-extension-requests" className="rounded-xl border border-sky-400/40 bg-sky-400/[0.05] p-5">
          <h3 className="flex items-center gap-2 font-display font-bold text-lg mb-3 text-sky-300">
            <CalendarClock size={18} /> Extension Requests <span className="text-white/40 text-sm font-normal">({extReqs.length})</span>
          </h3>
          <p className="text-white/50 text-xs mb-3">Customers asking for more time. Approve in one tap — the extension bills automatically and the agreement is auto-signed.</p>
          <div className="space-y-2">
            {extReqs.map((rq) => (
              <div key={rq.id} data-testid={`ops-ext-req-${rq.id}`} className="flex flex-wrap items-center justify-between gap-2 bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{rq.name} <span className="text-white/40 font-normal">· {rq.vehicle}</span></div>
                  <div className="text-white/45 text-xs mt-0.5">
                    +{rq.days} day{rq.days !== 1 ? "s" : ""}: {rq.prev_return_date} → <span className="text-sky-300 font-semibold">{rq.new_return_date}</span>
                    <span className="text-[#F2B705] font-semibold ml-2">est. ${Number(rq.estimated_total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    {rq.note && <span className="block text-white/35 italic mt-0.5">"{rq.note}"</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`ops-ext-approve-${rq.id}`} onClick={() => approveExt(rq)}
                    className="flex items-center gap-1.5 text-xs font-semibold border border-green-400/50 text-green-300 px-3 py-1.5 rounded hover:bg-green-400 hover:text-black transition-colors">
                    <CheckCircle2 size={13} /> Approve
                  </button>
                  <button data-testid={`ops-ext-decline-${rq.id}`} onClick={() => declineExt(rq)}
                    className="text-xs font-semibold border border-red-400/40 text-red-300 px-3 py-1.5 rounded hover:bg-red-400 hover:text-black transition-colors">
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {actionNeeded.length > 0 && (
        <div data-testid="ops-action-needed" className="rounded-xl border border-[#F2B705]/40 bg-[#F2B705]/[0.06] p-5">
          <h3 className="flex items-center gap-2 font-display font-bold text-lg mb-3 text-[#F2B705]">
            <BellRing size={18} /> Action Needed <span className="text-white/40 text-sm font-normal">({actionNeeded.length})</span>
          </h3>
          <p className="text-white/50 text-xs mb-3">These bookings are behind their dates. Tap to advance the status in one click.</p>
          <div className="space-y-2">
            {actionNeeded.map((a) => (
              <div key={a.booking_id} data-testid={`ops-action-${a.booking_id}`} className="flex flex-wrap items-center justify-between gap-2 bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{a.name} <span className="text-white/40 font-normal">· {a.vehicle}</span></div>
                  <div className="text-white/45 text-xs mt-0.5 flex items-center gap-3">
                    <span>{a.type === "pickup" ? `Pickup ${a.pickup_date}` : `Return ${a.return_date}`}</span>
                    {a.phone && <a href={`tel:${a.phone}`} className="flex items-center gap-1 text-[#F2B705] hover:underline"><Phone size={11} /> {a.phone}</a>}
                  </div>
                </div>
                <button data-testid={`ops-action-advance-${a.booking_id}`}
                  onClick={() => a.type === "inspection" ? toast.message("Open this booking's Inspections to review the post-trip condition.") : advanceStatus(a.booking_id, a.suggested_status, a.label)}
                  className={`flex items-center gap-1.5 text-xs font-semibold border px-3 py-1.5 rounded transition-colors ${a.type === "inspection" ? "border-red-400/50 text-red-300 bg-red-400/[0.08] hover:bg-red-400 hover:text-black" : "border-[#F2B705]/50 text-[#F2B705] hover:bg-[#F2B705] hover:text-black"}`}>
                  {a.type === "inspection" ? "🔍" : "⏰"} {a.label}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={CarFront} label="Active Rentals" value={s.active_rentals} tone="ok" testid="stat-active" />
        <StatCard icon={AlertTriangle} label="Overdue" value={s.overdue} tone="danger" testid="stat-overdue" />
        <StatCard icon={CalendarClock} label="Due Today" value={s.due_today} tone="warn" testid="stat-due-today" />
        <StatCard icon={Clock} label="Due in 2 Days" value={s.due_soon} testid="stat-due-soon" />
        <StatCard icon={Inbox} label="New Bookings" value={s.new_bookings} testid="stat-new-bookings" />
        <StatCard icon={BellRing} label="Action Needed" value={s.action_needed || 0} tone="warn" testid="stat-action-needed" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <Section title="Overdue" icon={AlertTriangle} items={data.overdue} tone="overdue" empty="Nothing overdue. 🎉" onReturned={markReturned} />
          <Section title="Due Today" icon={CalendarClock} items={data.due_today} tone="today" empty="No returns due today." onReturned={markReturned} />
          <Section title="Due in 2 Days" icon={Clock} items={data.due_soon} tone="soon" empty="Nothing due in the next 2 days." onReturned={markReturned} />
        </div>
        <div className="space-y-6">
          <Section title="All Active Rentals" icon={CarFront} items={data.active_rentals} tone="active" empty="No active rentals right now." onReturned={markReturned} />

          <div>
            <h3 className="flex items-center gap-2 font-display font-bold text-lg mb-3"><CarFront size={18} className="text-white/60" /> Vehicle Availability</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {data.fleet_board.map((v) => (
                <div key={v.id} data-testid={`ops-fleet-${v.id}`} className="flex items-center justify-between bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5">
                  <span className="font-medium text-sm truncate">{v.year} {v.make} {v.model}</span>
                  <span className="flex items-center gap-2 text-xs capitalize text-white/65">
                    <span className={`w-2 h-2 rounded-full ${STATUS_DOT[v.status] || "bg-white/30"}`} />
                    {v.status}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex gap-4 mt-3 text-xs text-white/45">
              <span><b className="text-green-400">{s.fleet.available}</b> available</span>
              <span><b className="text-red-400">{s.fleet.rented}</b> rented</span>
              <span><b className="text-yellow-400">{s.fleet.maintenance}</b> maintenance</span>
            </div>
          </div>

          <div data-testid="ops-social-clicks">
            <h3 className="flex items-center gap-2 font-display font-bold text-lg mb-3"><Share2 size={18} className="text-white/60" /> Social Link Clicks</h3>
            {(!data.social_clicks || Object.keys(data.social_clicks).length === 0) ? (
              <p className="text-white/35 text-sm">No social link clicks yet. Clicks on your footer icons will tally here.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {Object.entries(data.social_clicks).map(([platform, count]) => (
                  <div key={platform} className="flex items-center justify-between bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5">
                    <span className="capitalize text-sm">{platform}</span>
                    <span className="font-display font-bold text-[#F2B705]">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
